import React, { useState, useEffect } from 'react';
import { BackofficeLayout } from '../../layouts/BackofficeLayout';
import { api } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import {
  CreditCard,
  Loader2,
  ChevronDown,
  ChevronRight,
  Users,
  Activity,
  Webhook,
  Crown,
  Zap,
  RefreshCw,
  CheckCircle2,
  XCircle,
  Clock,
  ArrowLeft,
  Search,
  ShieldAlert,
  Plus,
} from 'lucide-react';
import { Input } from '../../components/ui/input';

const planBadge = (plan) => {
  if (plan === 'pro') return <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30 font-mono text-[10px]" data-testid={`plan-badge-${plan}`}>PRO</Badge>;
  if (plan === 'single') return <Badge className="bg-blue-500/20 text-blue-400 border-blue-500/30 font-mono text-[10px]" data-testid={`plan-badge-${plan}`}>SINGLE</Badge>;
  return <Badge className="bg-slate-700/50 text-slate-500 border-slate-600/30 font-mono text-[10px]" data-testid={`plan-badge-${plan}`}>FREE</Badge>;
};

const statusBadge = (status) => {
  const map = {
    active: { cls: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30', icon: CheckCircle2 },
    paid: { cls: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30', icon: CheckCircle2 },
    canceled: { cls: 'bg-red-500/20 text-red-400 border-red-500/30', icon: XCircle },
    canceling: { cls: 'bg-amber-500/20 text-amber-400 border-amber-500/30', icon: Clock },
    past_due: { cls: 'bg-red-500/20 text-red-400 border-red-500/30', icon: XCircle },
    pending: { cls: 'bg-slate-600/30 text-slate-400 border-slate-600/30', icon: Clock },
    none: { cls: 'bg-slate-700/50 text-slate-500 border-slate-600/30', icon: null },
  };
  const m = map[status] || map.none;
  const Icon = m.icon;
  return (
    <Badge className={`${m.cls} font-mono text-[10px] gap-1`} data-testid={`status-badge-${status}`}>
      {Icon && <Icon className="w-3 h-3" />}
      {(status || 'none').toUpperCase()}
    </Badge>
  );
};

const fmtDate = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit' }) + ' ' + d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
};

const eventLabel = (ev) => {
  const t = ev.event_type || '';
  if (t === 'checkout.session.completed') {
    return ev.plan === 'pro' ? `Pro subscription started` : `Bid Check credit purchased`;
  }
  if (t === 'invoice.paid') return `Invoice paid (${ev.amount ? '£' + (ev.amount / 100).toFixed(0) : ''})`;
  if (t === 'customer.subscription.created') return 'Subscription created';
  if (t === 'customer.subscription.updated') return `Subscription updated → ${ev.subscription_status || ''}`;
  if (t === 'customer.subscription.deleted') return 'Subscription canceled — reverted to free';
  if (t === 'invoice.payment_failed') return `Payment failed (attempt ${ev.attempt_count || '?'})`;
  if (t === 'charge.refunded') return `Refund processed (${ev.amount_refunded ? '£' + (ev.amount_refunded / 100).toFixed(0) : ''})`;
  if (t === 'manual_admin_grant') return `Manual: ${ev.action || 'entitlement granted'} — ${ev.reason || ''}`;
  return t.replace(/\./g, ' ');
};

// --- User Detail Panel ---
const UserDetail = ({ orgId, onClose }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [granting, setGranting] = useState(false);

  const fetchDetail = () => {
    setLoading(true);
    api.get(`/backoffice/billing/user/${orgId}`)
      .then(r => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchDetail(); }, [orgId]);

  const grantEntitlement = async (action) => {
    const reason = prompt(`Reason for "${action}":`);
    if (!reason) return;
    setGranting(true);
    try {
      await api.post('/backoffice/billing/grant-entitlement', { org_id: orgId, action, reason });
      fetchDetail();
    } catch (e) {
      alert(`Grant failed: ${e.response?.data?.detail || e.message}`);
    } finally {
      setGranting(false);
    }
  };

  if (loading) return <div className="flex items-center justify-center py-12"><Loader2 className="w-5 h-5 animate-spin text-orange-500" /></div>;
  if (!data) return <p className="text-slate-500 py-4">Failed to load user details.</p>;

  const { subscription: sub, payments, events, user } = data;

  return (
    <div className="space-y-4" data-testid="user-detail-panel">
      <div className="flex items-center gap-2 mb-2">
        <Button variant="ghost" size="sm" onClick={onClose} className="text-slate-400 hover:text-white h-7 px-2">
          <ArrowLeft className="w-4 h-4" />
        </Button>
        <h2 className="text-sm font-bold text-white tracking-wider" style={{ fontFamily: 'Barlow Condensed' }}>
          {user?.email || orgId}
        </h2>
      </div>

      {/* Subscription state */}
      <Card className="bg-slate-900 border-slate-800">
        <CardHeader className="pb-2">
          <CardTitle className="text-xs text-slate-400 uppercase tracking-wider">Subscription State</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 text-sm">
          <div className="grid grid-cols-2 gap-x-6 gap-y-2">
            <div className="text-slate-500">Plan</div><div className="text-white">{planBadge(sub?.plan)}</div>
            <div className="text-slate-500">Status</div><div>{statusBadge(sub?.status)}</div>
            <div className="text-slate-500">Paid Credits</div><div className="text-white font-mono">{sub?.paid_credits ?? 0}</div>
            <div className="text-slate-500">Stripe Customer</div><div className="text-slate-300 font-mono text-xs break-all">{sub?.stripe_customer_id || '—'}</div>
            <div className="text-slate-500">Stripe Sub</div><div className="text-slate-300 font-mono text-xs break-all">{sub?.stripe_subscription_id || '—'}</div>
            <div className="text-slate-500">Last Invoice Paid</div><div className="text-slate-300 text-xs">{fmtDate(sub?.last_invoice_paid_at)}</div>
          </div>
          {/* Manual recovery actions */}
          <div className="mt-4 pt-3 border-t border-slate-800">
            <p className="text-[10px] uppercase tracking-[0.15em] text-slate-600 mb-2 flex items-center gap-1">
              <ShieldAlert className="w-3 h-3" /> Recovery Actions
            </p>
            <div className="flex gap-2 flex-wrap">
              <Button size="sm" variant="outline" disabled={granting} onClick={() => grantEntitlement('add_credit')}
                className="text-[10px] h-7 border-slate-700 text-slate-400 hover:text-white hover:border-emerald-500" data-testid="grant-credit-btn">
                <Plus className="w-3 h-3 mr-1" /> Add Credit
              </Button>
              <Button size="sm" variant="outline" disabled={granting} onClick={() => grantEntitlement('activate_pro')}
                className="text-[10px] h-7 border-slate-700 text-slate-400 hover:text-white hover:border-amber-500" data-testid="grant-pro-btn">
                <Crown className="w-3 h-3 mr-1" /> Activate Pro
              </Button>
              <Button size="sm" variant="outline" disabled={granting} onClick={() => grantEntitlement('reset_to_free')}
                className="text-[10px] h-7 border-slate-700 text-slate-400 hover:text-white hover:border-red-500" data-testid="grant-reset-btn">
                Reset to Free
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Payment History */}
      <Card className="bg-slate-900 border-slate-800">
        <CardHeader className="pb-2">
          <CardTitle className="text-xs text-slate-400 uppercase tracking-wider">Payment History</CardTitle>
        </CardHeader>
        <CardContent>
          {payments.length === 0 ? (
            <p className="text-slate-600 text-xs">No payments recorded.</p>
          ) : (
            <div className="space-y-1">
              {payments.map((tx, i) => (
                <div key={i} className="flex items-center justify-between py-1.5 border-b border-slate-800/50 last:border-0">
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-slate-300">{tx.plan === 'pro' ? 'Pro £149/mo' : 'Single £9'}</span>
                    {tx.payment_status === 'paid' ? (
                      <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30 font-mono text-[10px]">PAID</Badge>
                    ) : (
                      <Badge className="bg-slate-600/30 text-slate-400 border-slate-600/30 font-mono text-[10px]">{(tx.payment_status || 'pending').toUpperCase()}</Badge>
                    )}
                    {tx.fulfilled_at && <CheckCircle2 className="w-3 h-3 text-emerald-500" />}
                  </div>
                  <span className="text-[10px] text-slate-600 font-mono">{fmtDate(tx.created_at)}</span>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Webhook Events */}
      <Card className="bg-slate-900 border-slate-800">
        <CardHeader className="pb-2">
          <CardTitle className="text-xs text-slate-400 uppercase tracking-wider flex items-center gap-1.5">
            <Webhook className="w-3 h-3" /> Webhook Events
          </CardTitle>
        </CardHeader>
        <CardContent>
          {events.length === 0 ? (
            <p className="text-slate-600 text-xs">No webhook events logged yet.</p>
          ) : (
            <div className="space-y-1">
              {events.map((ev, i) => (
                <div key={i} className="flex items-center justify-between py-1.5 border-b border-slate-800/50 last:border-0">
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] font-mono text-orange-400/80">{ev.event_type}</span>
                    {'fulfilled' in ev && (
                      ev.fulfilled ? <CheckCircle2 className="w-3 h-3 text-emerald-500" /> : <XCircle className="w-3 h-3 text-red-400" />
                    )}
                  </div>
                  <span className="text-[10px] text-slate-600 font-mono">{fmtDate(ev.timestamp)}</span>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// --- Main Page ---
export default function BillingAdmin() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [searchEmail, setSearchEmail] = useState('');
  const [searchResult, setSearchResult] = useState(null);
  const [searching, setSearching] = useState(false);

  const fetchData = async () => {
    try {
      const res = await api.get('/backoffice/billing/overview');
      setData(res.data);
    } catch (e) {
      console.error('Failed to fetch billing overview', e);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchData();
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!searchEmail.includes('@')) return;
    setSearching(true);
    setSearchResult(null);
    try {
      const res = await api.get(`/backoffice/billing/search?email=${encodeURIComponent(searchEmail)}`);
      setSearchResult(res.data);
      if (res.data.found && res.data.org_id) {
        setSelectedOrg(res.data.org_id);
      }
    } catch (e) {
      console.error('Search failed', e);
    } finally {
      setSearching(false);
    }
  };

  if (loading) {
    return (
      <BackofficeLayout>
        <div className="flex items-center justify-center h-[60vh]">
          <Loader2 className="w-6 h-6 animate-spin text-orange-500" />
        </div>
      </BackofficeLayout>
    );
  }

  // If user detail is selected, show that
  if (selectedOrg) {
    return (
      <BackofficeLayout>
        <UserDetail orgId={selectedOrg} onClose={() => setSelectedOrg(null)} />
      </BackofficeLayout>
    );
  }

  const { users = [], activity = [], transactions = [], summary = {} } = data || {};

  return (
    <BackofficeLayout>
      <div data-testid="billing-admin-page" className="space-y-5">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-xl font-bold text-white tracking-tight" style={{ fontFamily: 'Barlow Condensed' }}>
              BILLING / PAYMENTS
            </h1>
            <p className="text-xs text-slate-500 mt-0.5">Stripe activity, entitlement state, and recovery tools</p>
          </div>
          <div className="flex items-center gap-2">
            <form onSubmit={handleSearch} className="flex gap-1.5">
              <Input
                type="email"
                placeholder="Search by email..."
                value={searchEmail}
                onChange={(e) => setSearchEmail(e.target.value)}
                className="bg-slate-800 border-slate-700 text-white text-xs h-8 w-48"
                data-testid="billing-search-input"
              />
              <Button type="submit" size="sm" disabled={searching} variant="ghost" className="text-slate-400 hover:text-white h-8 px-2" data-testid="billing-search-btn">
                {searching ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Search className="w-3.5 h-3.5" />}
              </Button>
            </form>
            {searchResult && !searchResult.found && (
              <span className="text-[10px] text-red-400">Not found</span>
            )}
            <Button variant="ghost" size="sm" onClick={handleRefresh} className="text-slate-400 hover:text-white h-8" data-testid="billing-refresh-btn">
              <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <Card className="bg-slate-900 border-slate-800">
            <CardContent className="p-4">
              <p className="text-[10px] text-slate-500 uppercase tracking-wider">Total Users</p>
              <p className="text-2xl font-bold font-mono text-slate-200 mt-1" data-testid="stat-total-users">{summary.total_users}</p>
            </CardContent>
          </Card>
          <Card className="bg-slate-900 border-slate-800">
            <CardContent className="p-4">
              <p className="text-[10px] text-slate-500 uppercase tracking-wider">Pro Subscribers</p>
              <p className="text-2xl font-bold font-mono text-amber-400 mt-1" data-testid="stat-pro-users">{summary.pro_users}</p>
            </CardContent>
          </Card>
          <Card className="bg-slate-900 border-slate-800">
            <CardContent className="p-4">
              <p className="text-[10px] text-slate-500 uppercase tracking-wider">Single Credits</p>
              <p className="text-2xl font-bold font-mono text-blue-400 mt-1" data-testid="stat-single-users">{summary.paid_single}</p>
            </CardContent>
          </Card>
          <Card className="bg-slate-900 border-slate-800">
            <CardContent className="p-4">
              <p className="text-[10px] text-slate-500 uppercase tracking-wider">Paid Transactions</p>
              <p className="text-2xl font-bold font-mono text-emerald-400 mt-1" data-testid="stat-paid-tx">{summary.total_paid_transactions}</p>
            </CardContent>
          </Card>
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-3 gap-5">
          {/* Users Table — 2 cols wide */}
          <div className="xl:col-span-2">
            <Card className="bg-slate-900 border-slate-800">
              <CardHeader className="pb-2">
                <CardTitle className="text-xs text-slate-400 uppercase tracking-wider flex items-center gap-1.5">
                  <Users className="w-3.5 h-3.5" /> All Users ({users.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full text-xs" data-testid="billing-users-table">
                    <thead>
                      <tr className="border-b border-slate-800 text-slate-500 text-left">
                        <th className="px-4 py-2.5 font-medium">Email</th>
                        <th className="px-3 py-2.5 font-medium">Plan</th>
                        <th className="px-3 py-2.5 font-medium">Status</th>
                        <th className="px-3 py-2.5 font-medium">Credits</th>
                        <th className="px-3 py-2.5 font-medium hidden lg:table-cell">Stripe Customer</th>
                        <th className="px-3 py-2.5 font-medium hidden lg:table-cell">Last Payment</th>
                        <th className="px-3 py-2.5 font-medium"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {users.map((u, i) => (
                        <tr
                          key={u.user_id || i}
                          className="border-b border-slate-800/50 hover:bg-slate-800/30 transition-colors cursor-pointer"
                          onClick={() => u.org_id && setSelectedOrg(u.org_id)}
                          data-testid={`user-row-${u.email}`}
                        >
                          <td className="px-4 py-2.5">
                            <div>
                              <span className="text-slate-200 font-medium">{u.email}</span>
                              {u.org_name && <span className="text-slate-600 ml-1.5">({u.org_name})</span>}
                            </div>
                          </td>
                          <td className="px-3 py-2.5">{planBadge(u.plan)}</td>
                          <td className="px-3 py-2.5">{statusBadge(u.status)}</td>
                          <td className="px-3 py-2.5 font-mono text-slate-300">
                            {u.plan === 'pro' ? <span className="text-amber-400">unlimited</span> : u.paid_credits}
                          </td>
                          <td className="px-3 py-2.5 hidden lg:table-cell font-mono text-slate-500 text-[10px] max-w-[140px] truncate">
                            {u.stripe_customer_id || '—'}
                          </td>
                          <td className="px-3 py-2.5 hidden lg:table-cell text-slate-500 text-[10px] font-mono">
                            {fmtDate(u.last_invoice_paid_at || u.updated_at)}
                          </td>
                          <td className="px-3 py-2.5">
                            {u.org_id && <ChevronRight className="w-3.5 h-3.5 text-slate-600" />}
                          </td>
                        </tr>
                      ))}
                      {users.length === 0 && (
                        <tr><td colSpan={7} className="px-4 py-8 text-center text-slate-600">No users found.</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Activity Feed — 1 col */}
          <div className="space-y-4">
            <Card className="bg-slate-900 border-slate-800">
              <CardHeader className="pb-2">
                <CardTitle className="text-xs text-slate-400 uppercase tracking-wider flex items-center gap-1.5">
                  <Activity className="w-3.5 h-3.5" /> Recent Activity
                </CardTitle>
              </CardHeader>
              <CardContent>
                {activity.length > 0 ? (
                  <div className="space-y-1.5" data-testid="activity-feed">
                    {activity.map((ev, i) => (
                      <div key={i} className="flex items-start gap-2 py-1.5 border-b border-slate-800/40 last:border-0">
                        <Webhook className="w-3 h-3 text-orange-500/60 mt-0.5 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="text-xs text-slate-300 leading-tight">{eventLabel(ev)}</p>
                          <p className="text-[10px] text-slate-600 font-mono mt-0.5">
                            {fmtDate(ev.timestamp)} {ev.org_id && <span className="text-slate-700">| {ev.org_id.slice(0, 8)}...</span>}
                          </p>
                        </div>
                        {'fulfilled' in ev && (
                          ev.fulfilled
                            ? <CheckCircle2 className="w-3 h-3 text-emerald-500 flex-shrink-0 mt-0.5" />
                            : <XCircle className="w-3 h-3 text-red-400 flex-shrink-0 mt-0.5" />
                        )}
                      </div>
                    ))}
                  </div>
                ) : transactions.length > 0 ? (
                  <div className="space-y-1.5" data-testid="activity-feed-tx">
                    {transactions.map((tx, i) => (
                      <div key={i} className="flex items-start gap-2 py-1.5 border-b border-slate-800/40 last:border-0">
                        <CreditCard className="w-3 h-3 text-blue-500/60 mt-0.5 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="text-xs text-slate-300">{tx.plan === 'pro' ? 'Pro £149/mo' : 'Single £9'} — {tx.payment_status}</p>
                          <p className="text-[10px] text-slate-600 font-mono mt-0.5">{fmtDate(tx.created_at)}</p>
                        </div>
                        {tx.fulfilled_at ? <CheckCircle2 className="w-3 h-3 text-emerald-500 flex-shrink-0 mt-0.5" /> : <Clock className="w-3 h-3 text-slate-600 flex-shrink-0 mt-0.5" />}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-slate-600 text-xs py-4 text-center">No billing activity yet.</p>
                )}
              </CardContent>
            </Card>

            {/* Webhook Processing Status */}
            <Card className="bg-slate-900 border-slate-800">
              <CardHeader className="pb-2">
                <CardTitle className="text-xs text-slate-400 uppercase tracking-wider flex items-center gap-1.5">
                  <Webhook className="w-3.5 h-3.5" /> Webhook Processing
                </CardTitle>
              </CardHeader>
              <CardContent>
                {transactions.length > 0 ? (
                  <div className="space-y-1.5" data-testid="webhook-status-list">
                    {transactions.slice(0, 10).map((tx, i) => {
                      const lastEvent = activity.find(a => a.session_id === tx.session_id);
                      return (
                        <div key={i} className="flex items-center justify-between py-1.5 border-b border-slate-800/40 last:border-0">
                          <div className="flex items-center gap-2 min-w-0">
                            <span className="text-[10px] font-mono text-slate-400 truncate max-w-[120px]">{tx.session_id?.slice(-12)}</span>
                            <span className="text-[10px] text-slate-600">{tx.plan === 'pro' ? 'Pro' : '£9'}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            {tx.fulfilled_at ? (
                              <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30 font-mono text-[10px] gap-0.5">
                                <CheckCircle2 className="w-2.5 h-2.5" /> FULFILLED
                              </Badge>
                            ) : (
                              <Badge className="bg-slate-600/30 text-slate-400 border-slate-600/30 font-mono text-[10px] gap-0.5">
                                <Clock className="w-2.5 h-2.5" /> PENDING
                              </Badge>
                            )}
                            {lastEvent && (
                              <span className="text-[10px] text-orange-400/60 font-mono hidden sm:inline">{lastEvent.event_type?.split('.').pop()}</span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-slate-600 text-xs py-4 text-center">No transactions to show.</p>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </BackofficeLayout>
  );
}
